import { __assign, __rest } from "tslib";
import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { BiDataHook } from '@wix/da-bi/pkg/constants';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useCommentPostAction } from '../../../CommentEditor/hooks/useCommentPostAction';
import { useCommentCancelAction } from '../../../CommentEditor/hooks/useCommentCancelAction';
import LoadingIndicator from '../../../LoadingIndicator';
import LoadingPlaceholder from '../LoadingPlaceholder';
import LazyPlaceholder from '../LazyPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import { CommentEditorContext, EditorContext, } from '../../../CommentEditor/context';
import CommentEditor from '../../../CommentEditor';
import s from './CommentBoxEditor.scss';
/** The core of our comment box. This is a `<CommentEditor />` with a footer and a loading indicator */
var CommentBoxEditor = function (_a) {
    var className = _a.className, footerClassName = _a.footerClassName, nc = _a.nc, onCancel = _a.onCancel, onPost = _a.onPost, restProps = __rest(_a, ["className", "footerClassName", "nc", "onCancel", "onPost"]);
    var isMounted = useIsMounted();
    var _b = useContext(CommentEditorContext), editorId = _b.editorId, isBusy = _b.isBusy, commentableItemId = _b.commentableItemId, commentableType = _b.commentableType, shouldUseFallback = _b.shouldUseFallback, isExpanded = _b.isExpanded, isFocusRequested = _b.isFocusRequested;
    var isLoadingTriggered = useContext(LoadableComponentContext).isLoadingTriggered;
    var api = useContext(EditorContext).api;
    useEffect(function () {
        if (!api) {
            return;
        }
        if (isFocusRequested) {
            ddt.log('comments', 'Focusing CommentBox editor in InView.onChange, id: ', editorId);
            api.focusEditor();
        }
    }, [isFocusRequested, api, editorId]);
    var postComment = useCommentPostAction({
        messageId: nc === null || nc === void 0 ? void 0 : nc.messageId,
        folderId: nc === null || nc === void 0 ? void 0 : nc.folderId,
        shouldDeleteMessage: nc === null || nc === void 0 ? void 0 : nc.shouldDeleteMessage,
        onPost: onPost,
    });
    var cancelComment = useCommentCancelAction({ onCancel: onCancel });
    return (React.createElement("div", { 
        /*
          This key is necessary to get the fallback editor working for older safari versions.
  
          The short of it is that old safari is weird and doesn't force a rerender when `shouldUseFallback` changes on the frontend.
  
          Now why did this take so long to fix and why did I use `isMounted` instead of `shouldUseFallback` as a key? I tried basing the key on `shouldUseFallback` (which definitely changes between SSR and clientside rendering),
          but that didn't work for some unknown reason. I still haven't figured that one out. So -- under the assumption this wasn't a hydration issue -- I went on a wild goose chase of:
            1. Verifying that the css we're importing contained the correct class names -- it did
            2. Verifying `classnames` was returning the correct value -- it was
            3. Verifying that overlapping CSS class names weren't causing issues (i.e. `.expanded` being used on both the editor and the wrapping element) - they weren't
            4. Triple checking that `shouldUseFallback` was DEFINITELY being updated on the client in old safari -- it is
            5. Checking the entire component tree to make sure a weird React.memo wasn't causing issues -- no memos to be seen.
            X. So many other things I won't bother listing.
  
          But in a fit of desperation I tried a different client-side-updating variable to reference in the key and behold! It works.
        */
        key: 
        // Guard against rerendering outside of fallback contexts as well by checking `shouldUseFallback` before
        // passing a key in
        shouldUseFallback ? "safari-force-rerender-key-".concat(isMounted) : undefined, className: classnames(s['root'], (isExpanded || shouldUseFallback) && s['expanded'], shouldUseFallback && s['fallback'], className), "data-hook": BiDataHook.COMMENT_EDITOR },
        isBusy && React.createElement(LoadingIndicator, { className: s['loading'] }),
        !isLoadingTriggered && !shouldUseFallback && React.createElement(LazyPlaceholder, null),
        React.createElement(CommentEditor, __assign({ className: classnames(s['commentbox-editor'], (isExpanded || shouldUseFallback) && s['expanded']), loadingIndicator: React.createElement(LoadingPlaceholder, null), onKeyboardSubmit: postComment }, restProps)),
        (shouldUseFallback || isLoadingTriggered) && (React.createElement(BiLoggerContextProvider, { value: {
                itemid: typeof commentableItemId === 'string'
                    ? parseInt(commentableItemId)
                    : commentableItemId,
                typeid: commentableType,
            } },
            React.createElement(CommentBoxFooter, { onCancel: cancelComment, onPost: postComment, className: classnames(s['commentbox-footer'], footerClassName) }))),
        nc && nc.postfix));
};
CommentBoxEditor.displayName = 'CommentBoxEditor';
export default CommentBoxEditor;
//# sourceMappingURL=CommentBoxEditor.js.map